/**
 * Central booking-wizard state for the retreat modal — a port of the tenant
 * side `booking-modal/useBookingState.ts` to Preact hooks.
 *
 * The one behavioural change from the tenant version: participant validation
 * uses a small inline validator instead of the Zod `participantSchema`, so the
 * widget bundle doesn't pull Zod in. The rules mirror the schema exactly
 * (firstName/lastName 1-100, email valid + <=255, phone <=20, dietary <=500).
 */

import { useCallback, useMemo, useReducer } from 'preact/hooks'
import type {
  CustomQuestion,
  PaymentMode,
  RetreatData,
  RetreatTier,
  RoommatePreference,
} from '../types'

export type StepNum = 1 | 2 | 3 | 4

export interface ParticipantDraft {
  firstName: string
  lastName: string
  email: string
  phone: string
  dietaryNotes: string
}

export function emptyParticipant(): ParticipantDraft {
  return { firstName: '', lastName: '', email: '', phone: '', dietaryNotes: '' }
}

export type ParticipantField = keyof ParticipantDraft

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Which fields of a participant are invalid. Mirrors the server participantSchema. */
export function invalidParticipantFields(p: ParticipantDraft): Set<ParticipantField> {
  const bad = new Set<ParticipantField>()
  const first = p.firstName.trim()
  const last = p.lastName.trim()
  const email = p.email.trim()
  const phone = p.phone.trim()
  const dietary = p.dietaryNotes.trim()
  if (first.length < 1 || first.length > 100) bad.add('firstName')
  if (last.length < 1 || last.length > 100) bad.add('lastName')
  if (email.length < 1 || email.length > 255 || !EMAIL_RE.test(email)) bad.add('email')
  if (phone.length > 20) bad.add('phone')
  if (dietary.length > 500) bad.add('dietaryNotes')
  return bad
}

export interface BookingState {
  step: StepNum
  participantCount: 1 | 2
  participants: ParticipantDraft[]
  customAnswers: Record<string, string>
  roommatePreference: RoommatePreference | null
  roommateKnownName: string
  paymentMode: PaymentMode
  giftCardCode: string
  couponCode: string
  disclosureAccepted: boolean
}

type Action =
  | { type: 'SET_STEP'; step: StepNum }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_PARTICIPANT_COUNT'; count: 1 | 2 }
  | { type: 'UPDATE_PARTICIPANT'; index: number; fields: Partial<ParticipantDraft> }
  | { type: 'SET_ANSWER'; key: string; value: string }
  | { type: 'SET_ROOMMATE_PREFERENCE'; value: RoommatePreference | null }
  | { type: 'SET_ROOMMATE_KNOWN_NAME'; value: string }
  | { type: 'SET_PAYMENT_MODE'; value: PaymentMode }
  | { type: 'SET_GIFT_CARD_CODE'; value: string }
  | { type: 'SET_COUPON_CODE'; value: string }
  | { type: 'SET_DISCLOSURE_ACCEPTED'; value: boolean }

function clampStep(n: number): StepNum {
  if (n < 1) return 1
  if (n > 4) return 4
  return n as StepNum
}

function reducer(state: BookingState, action: Action): BookingState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step }
    case 'NEXT_STEP':
      return { ...state, step: clampStep(state.step + 1) }
    case 'PREV_STEP':
      return { ...state, step: clampStep(state.step - 1) }
    case 'SET_PARTICIPANT_COUNT': {
      const count = action.count
      const participants = [...state.participants]
      if (count === 2 && participants.length === 1) {
        participants.push(emptyParticipant())
      } else if (count === 1 && participants.length === 2) {
        participants.pop()
      }
      return { ...state, participantCount: count, participants }
    }
    case 'UPDATE_PARTICIPANT': {
      const participants = state.participants.map((p, i) =>
        i === action.index ? { ...p, ...action.fields } : p,
      )
      return { ...state, participants }
    }
    case 'SET_ANSWER':
      return { ...state, customAnswers: { ...state.customAnswers, [action.key]: action.value } }
    case 'SET_ROOMMATE_PREFERENCE':
      return { ...state, roommatePreference: action.value }
    case 'SET_ROOMMATE_KNOWN_NAME':
      return { ...state, roommateKnownName: action.value }
    case 'SET_PAYMENT_MODE':
      return { ...state, paymentMode: action.value }
    case 'SET_GIFT_CARD_CODE':
      return { ...state, giftCardCode: action.value }
    case 'SET_COUPON_CODE':
      return { ...state, couponCode: action.value }
    case 'SET_DISCLOSURE_ACCEPTED':
      return { ...state, disclosureAccepted: action.value }
    default:
      return state
  }
}

export interface UseBookingStateOptions {
  retreat: RetreatData
  tier: RetreatTier
  prefillUser?: { firstName?: string | null; lastName?: string | null; email?: string | null }
}

export function pickInitialPaymentMode(retreat: RetreatData): PaymentMode {
  // Et event betales altid fuldt (V524), som tenant-sidens widget og serveren.
  if (retreat.kind === 'event') return 'full'
  if (retreat.allowFullPayment) return 'full'
  if (retreat.depositPercent && retreat.depositPercent > 0) return 'deposit'
  if (retreat.installmentsEnabled && retreat.installmentsCount) return 'installments'
  return 'full'
}

/**
 * Værelseskammerat-valget vises kun for en enkelt person på et delt dobbeltværelse, og
 * aldrig på et event: et event har ingen værelser, og serveren gemmer ingen
 * værelseskammerat på et event (qa-events-opfoelgning).
 */
export function showRoommateOptions(retreat: RetreatData, tier: RetreatTier, participantCount: number): boolean {
  return retreat.kind !== 'event' && tier.occupancy === 'shared_double' && participantCount === 1
}

/** Er betalingsformen tilladt? Som serverens validatePaymentMode: et event betales altid fuldt. */
export function paymentModeAllowed(retreat: RetreatData, mode: PaymentMode): boolean {
  if (mode === 'free') return true
  if (retreat.kind === 'event') return mode === 'full'
  if (mode === 'full') return !!retreat.allowFullPayment
  if (mode === 'deposit') return !!retreat.depositPercent && retreat.depositPercent > 0
  if (mode === 'installments') {
    return !!retreat.installmentsEnabled && !!retreat.installmentsCount && retreat.installmentsCount >= 2
  }
  return false
}

function pickInitialParticipantCount(tier: RetreatTier): 1 | 2 {
  if (tier.spotsPerBookingMin >= 2) return 2
  return 1
}

export interface BookingStateApi {
  state: BookingState
  setStep: (step: StepNum) => void
  nextStep: () => void
  prevStep: () => void
  setParticipantCount: (count: 1 | 2) => void
  updateParticipant: (index: number, fields: Partial<ParticipantDraft>) => void
  setAnswer: (key: string, value: string) => void
  setRoommatePreference: (value: RoommatePreference | null) => void
  setRoommateKnownName: (value: string) => void
  setPaymentMode: (value: PaymentMode) => void
  setGiftCardCode: (value: string) => void
  setCouponCode: (value: string) => void
  setDisclosureAccepted: (value: boolean) => void
  /** `free`: købet koster 0 (sendes som 'free'), så betalingsformen tjekkes ikke. */
  validateStep: (step: StepNum, opts?: { free?: boolean }) => string[]
  canProceed: boolean
  visibleQuestions: CustomQuestion[]
  shouldShowRoommateOptions: boolean
  isPrivateDoubleSolo: boolean
}

export function useBookingState(opts: UseBookingStateOptions): BookingStateApi {
  const { retreat, tier, prefillUser } = opts

  const initial = useMemo<BookingState>(() => {
    const count = pickInitialParticipantCount(tier)
    const first = emptyParticipant()
    if (prefillUser) {
      first.firstName = prefillUser.firstName ?? ''
      first.lastName = prefillUser.lastName ?? ''
      first.email = prefillUser.email ?? ''
    }
    const participants: ParticipantDraft[] = count === 2 ? [first, emptyParticipant()] : [first]
    return {
      step: 1,
      participantCount: count,
      participants,
      customAnswers: {},
      roommatePreference: retreat.kind !== 'event' && tier.occupancy === 'shared_double' ? 'match_me' : null,
      roommateKnownName: '',
      paymentMode: pickInitialPaymentMode(retreat),
      giftCardCode: '',
      couponCode: '',
      disclosureAccepted: false,
    }
    // Only initialise once per modal mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [state, dispatch] = useReducer(reducer, initial)

  const setStep = useCallback((step: StepNum) => dispatch({ type: 'SET_STEP', step }), [])
  const nextStep = useCallback(() => dispatch({ type: 'NEXT_STEP' }), [])
  const prevStep = useCallback(() => dispatch({ type: 'PREV_STEP' }), [])
  const setParticipantCount = useCallback(
    (count: 1 | 2) => dispatch({ type: 'SET_PARTICIPANT_COUNT', count }),
    [],
  )
  const updateParticipant = useCallback(
    (index: number, fields: Partial<ParticipantDraft>) =>
      dispatch({ type: 'UPDATE_PARTICIPANT', index, fields }),
    [],
  )
  const setAnswer = useCallback(
    (key: string, value: string) => dispatch({ type: 'SET_ANSWER', key, value }),
    [],
  )
  const setRoommatePreference = useCallback(
    (value: RoommatePreference | null) => dispatch({ type: 'SET_ROOMMATE_PREFERENCE', value }),
    [],
  )
  const setRoommateKnownName = useCallback(
    (value: string) => dispatch({ type: 'SET_ROOMMATE_KNOWN_NAME', value }),
    [],
  )
  const setPaymentMode = useCallback(
    (value: PaymentMode) => dispatch({ type: 'SET_PAYMENT_MODE', value }),
    [],
  )
  const setGiftCardCode = useCallback(
    (value: string) => dispatch({ type: 'SET_GIFT_CARD_CODE', value }),
    [],
  )
  const setCouponCode = useCallback(
    (value: string) => dispatch({ type: 'SET_COUPON_CODE', value }),
    [],
  )
  const setDisclosureAccepted = useCallback(
    (value: boolean) => dispatch({ type: 'SET_DISCLOSURE_ACCEPTED', value }),
    [],
  )

  const visibleQuestions = useMemo(() => retreat.customQuestions ?? [], [retreat.customQuestions])

  const isEvent = retreat.kind === 'event'
  const shouldShowRoommateOptions = showRoommateOptions(retreat, tier, state.participantCount)

  const isPrivateDoubleSolo =
    !isEvent && tier.occupancy === 'private_double' && state.participantCount === 1

  const validateStep = useCallback(
    (step: StepNum, opts?: { free?: boolean }): string[] => {
      const errors: string[] = []
      if (step === 1) {
        if (
          state.participantCount < tier.spotsPerBookingMin ||
          state.participantCount > tier.spotsPerBookingMax
        ) {
          errors.push('errors.participantCountOutOfRange')
        }
        if (shouldShowRoommateOptions) {
          if (!state.roommatePreference) {
            errors.push('errors.roommatePreferenceRequired')
          }
          if (
            state.roommatePreference === 'known_partner' &&
            state.roommateKnownName.trim().length === 0
          ) {
            errors.push('errors.roommateKnownNameRequired')
          }
        }
      }
      if (step === 2) {
        state.participants.forEach((p, idx) => {
          if (invalidParticipantFields(p).size > 0) {
            errors.push(`errors.participant.${idx}`)
          }
        })
      }
      if (step === 3) {
        visibleQuestions.forEach((q) => {
          if (q.required) {
            const answer = state.customAnswers[q.key]?.trim() ?? ''
            if (answer.length === 0) {
              errors.push(`errors.question.${q.key}`)
            }
          }
        })
      }
      if (step === 4) {
        // Samme regel som serveren (validatePaymentMode); et gratis køb sendes som 'free'.
        if (!opts?.free && !paymentModeAllowed(retreat, state.paymentMode)) {
          errors.push('errors.paymentModeUnavailable')
        }
        if (retreat.packageTravel && !state.disclosureAccepted) {
          errors.push('errors.disclosureRequired')
        }
      }
      return errors
    },
    [state, tier, retreat, visibleQuestions, shouldShowRoommateOptions],
  )

  const canProceed = useMemo(() => validateStep(state.step).length === 0, [validateStep, state.step])

  return {
    state,
    setStep,
    nextStep,
    prevStep,
    setParticipantCount,
    updateParticipant,
    setAnswer,
    setRoommatePreference,
    setRoommateKnownName,
    setPaymentMode,
    setGiftCardCode,
    setCouponCode,
    setDisclosureAccepted,
    validateStep,
    canProceed,
    visibleQuestions,
    shouldShowRoommateOptions,
    isPrivateDoubleSolo,
  }
}
